import * as React from "react"
import { FaUser } from "react-icons/fa"
import { gql } from "@apollo/client"
import {
  Avatar,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Link,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react"
import NextLink from "next/link"

import { CompetitionApplicantFragment } from "lib/graphql"

const _ = gql`
  fragment CompetitionApplicant on Applicant {
    id
    category
    members {
      id
      firstName
      avatarUrl
      privacySetting
    }
  }
`

interface Props {
  applicants: CompetitionApplicantFragment[]
  count: number
  isFinal: boolean
}

export function CompetitionApplicants({ applicants, isFinal, count }: Props) {
  const [showAll, setShowAll] = React.useState(false)

  const groupedApplicants = React.useMemo(
    () =>
      applicants?.reduce((acc, applicant) => {
        const category = applicant.category
        if (acc[category]) {
          acc[category].push(applicant)
        } else {
          acc[category] = [applicant]
        }
        return acc
      }, {} as { [category: string]: CompetitionApplicantFragment[] }),
    [applicants],
  )
  return (
    <>
      <Stack spacing={4} overflow="hidden" mt={24}>
        <Flex fontWeight="bold" justifyContent="space-between" pb={6}>
          {count > 0 && (
            <Heading textTransform="uppercase" fontWeight="black" fontSize="5xl">{`${
              isFinal ? "Finalisten " : "Attending "
            } (${count})`}</Heading>
          )}
        </Flex>

        {groupedApplicants &&
          Object.entries(groupedApplicants).map(([category, applicants], i) => {
            if (!showAll && i > 0) return null
            return (
              <Box key={category}>
                <Flex m={0} w="100%" justifyContent="space-between" alignItems="flex-end">
                  <Text fontWeight="black" textTransform="uppercase" fontSize="3xl" color="yellow.500">
                    {category} ({applicants.flatMap((application) => application.members).length})
                  </Text>
                </Flex>
                <SimpleGrid columns={{ base: 4, sm: 6, md: 8, lg: 10 }} spacing={5} py={4} w="full">
                  {applicants.map((applicant) =>
                    applicant.members.map((member) => (
                      <Tooltip
                        key={member.id}
                        label={member.firstName}
                        bg="yellow.500"
                        color="black"
                        fontWeight="bold"
                      >
                        <Box display="flex" justifyContent="center" mb={2} minW={0} w="full">
                          <Link
                            as={NextLink}
                            href={`/users/${member.id}`}
                            passHref
                            display="block"
                            maxW={{ base: "80px", md: "100px" }}
                            w="full"
                            _hover={{ textDecor: "none" }}
                          >
                            <Avatar
                              src={member.avatarUrl || undefined}
                              name={member.firstName || "avatar"}
                              w="full"
                              h="auto"
                              maxW={{ base: "80px", md: "100px" }}
                              aspectRatio={1}
                              bg="gray.600"
                              icon={<FaUser color="gray.900" size={24} />}
                              color="white"
                              aria-label={`Foto van ${member.firstName}`}
                            />
                          </Link>
                        </Box>
                      </Tooltip>
                    )),
                  )}
                </SimpleGrid>
              </Box>
            )
          })}
        {groupedApplicants && Object.keys(groupedApplicants).length > 1 && !showAll && (
          <Button variant="ghost" onClick={() => setShowAll(!showAll)} w="fit-content">
            Zie alle categorieën...
          </Button>
        )}
      </Stack>
      <Divider />
    </>
  )
}
